import { useState, useEffect, useCallback } from 'react';

const CopyEditor = ({
  value = '',
  onChange,
  placeholder = 'Start writing...',
  label,
  maxLength,
  minLength,
  isDark = false,
  autoFocus = false,
  rows = 4,
  showCharCount = true,
  showWordCount = true,
  showReadingTime = true,
  disabled = false,
  style = {},
  // Where the draft is saved. Pass one per exercise and field (e.g. "<exercise id>:body") so a draft
  // never reappears in another exercise; without it the field label is used, as before.
  draftKey,
}) => {
  const [draft, setDraft] = useState(value);
  const storageKey = `copy-draft-${draftKey || label || 'default'}`;

  // Auto-save to localStorage (it can be unavailable, e.g. blocked storage: then nothing is saved)
  useEffect(() => {
    let saved = null;
    try { saved = localStorage.getItem(storageKey); } catch { /* storage unavailable */ }
    if (saved && !value) {
      setDraft(saved);
      onChange?.(saved);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      try { if (draft) localStorage.setItem(storageKey, draft); } catch { /* storage unavailable */ }
    }, 500);
    return () => clearTimeout(timer);
  }, [draft, storageKey]);

  const handleChange = useCallback((e) => {
    const val = e.target.value;
    if (maxLength && val.length > maxLength) return;
    setDraft(val);
    onChange?.(val);
  }, [maxLength, onChange]);

  const wordCount = draft.trim() ? draft.trim().split(/\s+/).length : 0;
  const charCount = draft.length;
  const readingTime = Math.ceil(wordCount / 200); // avg reading speed

  const c = {
    card: isDark ? '#1c1917' : '#ffffff',
    border: isDark ? '#2d2825' : '#e8e4df',
    head: isDark ? '#fafaf9' : '#1c1917',
    muted: isDark ? '#78716c' : '#a8a29e',
    sub: isDark ? '#57534e' : '#b4aca6',
    inputBg: isDark ? '#0e0d0c' : '#faf9f7',
    inputBorder: isDark ? '#2d2825' : '#e8e4df',
    inputText: isDark ? '#d4cdc7' : '#44403c',
    placeholder: isDark ? '#57534e' : '#b4aca6',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, ...style }}>
      {label && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: c.muted, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            {label}
          </span>
          {maxLength && (
            <span style={{ fontSize: 10, fontWeight: 600, color: charCount > maxLength * 0.9 ? '#ef4444' : c.muted }}>
              {charCount}/{maxLength}
            </span>
          )}
        </div>
      )}

      <textarea
        value={draft}
        onChange={handleChange}
        placeholder={placeholder}
        autoFocus={autoFocus}
        disabled={disabled}
        rows={rows}
        style={{
          width: '100%',
          padding: '14px 16px',
          borderRadius: 12,
          border: `1.5px solid ${c.inputBorder}`,
          background: c.inputBg,
          color: c.inputText,
          fontSize: 14,
          lineHeight: 1.65,
          fontFamily: 'Inter, system-ui, sans-serif',
          resize: 'vertical',
          minHeight: rows * 24,
          outline: 'none',
          transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
        }}
        onFocus={e => {
          e.target.style.borderColor = '#7c3aed';
          e.target.style.boxShadow = '0 0 0 3px rgba(124,58,237,0.1)';
        }}
        onBlur={e => {
          e.target.style.borderColor = c.inputBorder;
          e.target.style.boxShadow = 'none';
        }}
      />

      {/* Meta bar */}
      {(showCharCount || showWordCount || showReadingTime) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          {showWordCount && (
            <span style={{ fontSize: 10, fontWeight: 600, color: c.muted }}>
              {wordCount} word{wordCount !== 1 ? 's' : ''}
            </span>
          )}
          {showCharCount && (
            <span style={{ fontSize: 10, fontWeight: 600, color: c.muted }}>
              {charCount} char{charCount !== 1 ? 's' : ''}
            </span>
          )}
          {showReadingTime && wordCount > 0 && (
            <span style={{ fontSize: 10, fontWeight: 600, color: c.muted }}>
              ~{readingTime} min read
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default CopyEditor;
